import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const NorthwestEdmonton = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighbourhoodWithState = `${neighborhoodName}, ${state}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:About Paragraph">
          Northwest Edmonton,{' '}
          <LinkText href="/rental-listings/alberta">Alberta</LinkText> is known
          for its blend of residential communities and commercial developments,
          offering a balanced urban lifestyle. It encompasses diverse
          neighborhoods such as the bustling Westmount, family-friendly
          Cumberland, and the historic Griesbach. Residents enjoy easy access to
          amenities like shopping centers, parks such as Coronation Park and
          Rundle Park, and cultural attractions like the Telus World of Science.
          The area is also home to educational institutions and healthcare
          facilities, making it an attractive choice for families and
          professionals alike. Northwest Edmonton’s dynamic mix of community
          charm and modern conveniences makes it a sought-after region within
          the region.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo.hood.northwest-edmonton-edmonton:an apartment to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Renting In Paragraph">
            Finding {{ filterNameWithFallback }} in {{ neighborhoodName }}{' '}
            requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, signing a lease, and depositing rent. All
            this can add up to a lengthy endeavour if you’re not familiar with
            how to find an apartment in {{ neighborhoodName }}.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodName}
        cityNameWithState={neighbourhoodWithState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,317 - $1,675',
          bed2: '$1,755 - $2,340',
          bed3: '$2,350 - $2,800'
        }}
        rentReportCity="Alberta"
        rentReportLink="https://liv.rent/blog/rent-reports/alberta/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e9%98%bf%e5%b0%94%e4%bc%af%e5%a1%94/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Apartment Hunting Paragraph 1">
            Finding affordable rentals in {{ neighborhoodName }} is easier
            compared to other major cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>,
            especially if you’re looking for{' '}
            <LinkText href={links.under1000}>rentals under $1000</LinkText>.
            Along with effective negotiation skills, you may be able to lower
            the rent price further. For helpful tips on negotiating rent, refer
            to our guide on{' '}
            <LinkText href="https://liv.rent/blog/renters/how-to-negotiate-rent/">
              how to negotiate rent
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Apartment Hunting Paragraph 2">
            {{ neighborhoodName }} offers a diverse range of neighborhoods and
            municipalities to choose from when searching for rentals. Depending
            on your preferences, you can explore various rental types, such as{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>, and{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>. Whether
            you’re seeking <LinkText href={links.furnished}>furnished</LinkText>{' '}
            or <LinkText href={links.unfurnished}>unfurnished rentals</LinkText>
            , or even considering{' '}
            <LinkText href={links.longTerm}>long-term</LinkText> or{' '}
            <LinkText href={links.shortTerm}>short-term</LinkText> options,{' '}
            {{ neighborhoodName }} has a variety of choices to suit your needs.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Apartments for rent Paragraph">
              <LinkText href={links.apartment}>Apartments for rent</LinkText>{' '}
              are not common in {{ neighborhoodName }}. Typically, they are more
              suitable for singles, couples, or small families looking for
              modern amenities and less maintenance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Houses for rent Paragraph">
              Detached <LinkText href={links.house}>houses for rent</LinkText>{' '}
              are a popular form of housing in {{ neighborhoodName }}. They are
              suitable for families seeking more space or yard. Both partial
              houses and entire houses for rent are common, with a mix of older
              and newer homes depending on which part of the region you’re
              looking in.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Townhouses for rent Paragraph">
              Townhouses are also common in {{ neighborhoodName }}. They combine
              the best qualities of both apartments and whole houses. Typically
              less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ neighborhoodName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Basements for rent Paragraph">
              A popular alternative for{' '}
              <LinkText href="https://liv.rent/blog/livrent/top-tips-for-first-time-renters/">
                first-time renters
              </LinkText>{' '}
              and anyone else seeking relatively inexpensive rental housing are{' '}
              <LinkText href={links.basement}>
                basements for rent in {{ neighborhoodName }}
              </LinkText>
              . Since rent prices for apartments and houses can often get too
              expensive, many renters seek out basements for rent as a cheaper
              alternative. Homeowners often rent out their basements as a way to
              offset their mortgage payments. This makes it easy for renters to
              find relatively inexpensive basements for rent in all areas of the
              region.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Condos for rent Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in {{ neighborhoodName }}
              </LinkText>{' '}
              are not a common form of rental housing. These types of rental
              accommodation are perfect for those who like to be in the centre
              of the action, and don’t need too much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Rooms for rent Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ neighborhoodName }}
              </LinkText>{' '}
              are a great way to experience the best of the region on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Pet friendly rentals Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>
                cat-friendly rental units
              </LinkText>{' '}
              are available throughout the region, as well as properties where
              both are allowed. Keep in mind that some {{ neighborhoodName }}
              neighbourhoods are better suited to those with pets, and adjust
              your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Furnished rentals Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodName }}. Renters seeking furnished
              units should search for these units using filters, but do be
              prepared to pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Short term rentals Paragraph">
              {{ neighborhoodName }} does not carry the distinction of being
              Canada’s largest region and isn’t characterized by a wide range of
              job opportunities or an extensive educational sector. Instead, it
              offers a closer-knit community lifestyle, different from the
              nation’s metropolitan areas.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText>{' '}
              aren’t as prevalent in {{ neighborhoodName }} as they are in
              larger cities, particularly because it doesn’t experience the
              typical influx of students or seasonal workers. However, for those
              seeking a quieter, more rural experience or temporary lodging
              during work in the region, the available rental options may
              suffice. The appeal of short-term rentals is the flexibility,
              which is a key factor when{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short terms rentals are suitable
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Cheap rentals Paragraph">
              Compared to other Canadian cities, rent prices in{' '}
              {{ neighborhoodName }} are relatively cheaper. As a result, it
              shouldn’t be too hard to find something within your budget. If
              you’re looking for
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighbourhoodWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in{' '}
              {{ neighborhoodName }} where rent is considerably cheaper, so
              exploring these options is a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLivingIn = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Living In Paragraph">
          Living in Northwest Edmonton offers a vibrant and community-oriented
          lifestyle with a range of amenities and recreational opportunities.
          Residents enjoy proximity to parks like Hawrelak Park and the river
          valley, ideal for outdoor activities and family outings. The area
          boasts diverse dining options, shopping centers such as West Edmonton
          Mall, and cultural venues like the Royal Alberta Museum. Northwest
          Edmonton is well-connected by public transit and major roadways,
          enhancing accessibility to downtown and beyond. With a mix of
          established neighborhoods and newer developments, living here provides
          a balance of urban conveniences and suburban tranquility, appealing to
          a variety of lifestyles.
        </Trans>
      </Paragraph>
    )
  }

  const getLocalLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.northwest-edmonton-edmonton:Local Landmarks Paragraph">
          In Northwest Edmonton, one prominent local landmark is the West
          Edmonton Mall (WEM), renowned as North America’s largest shopping and
          entertainment complex. WEM offers a vast array of attractions,
          including an indoor amusement park, a water park, an ice rink,
          miniature golf, and hundreds of shops and restaurants. Beyond shopping
          and entertainment, WEM hosts events, concerts, and exhibitions, making
          it a cultural and recreational destination for locals and visitors
          alike. Its iconic glass-domed Fantasyland Hotel and themed sections
          like Galaxyland and World Waterpark add to its appeal, cementing WEM
          as a significant landmark in Northwest Edmonton.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodName}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: null,
    thingsToDo: null,
    restaurants: null,
    transportation: null,
    whereToStay: null,
    living: getLivingIn(),
    landmarks: getLocalLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
      preventRepeatedCityName
    />
  )
}

export default NorthwestEdmonton
